import React from "react";
import ReactDOM from "react-dom/client";
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    TextField,
    Box,
    Fade,
    Grid,
} from "@mui/material";
import { StyledEngineProvider } from "@mui/material/styles";
import ThemeProvider from "../../theme";
import Iconify from "../iconify";
import { enqueueSnackbar } from "notistack";

const ReservationPromptDialog = ({ onResolve, onReject, options = {} }) => {
    const [formData, setFormData] = React.useState({
        notes: "",
        adults: "",
        kids: "",
        babies: "",
    });
    const [open, setOpen] = React.useState(true);

    const {
        title = "Reservation Details",
        message = "Please enter the reservation details:",
        confirmText = "Confirm",
        cancelText = "Cancel",
    } = options;

    const handleInputChange = (field, value) => {
        setFormData((prev) => ({
            ...prev,
            [field]: value,
        }));
    };

    const handleNumberChange = (field, value) => {
        // Allow empty string, otherwise ensure it's a valid positive number
        if (value === "" || value === null || value === undefined) {
            handleInputChange(field, "");
        } else {
            const numValue = Math.max(0, parseInt(value) || 0);
            handleInputChange(field, numValue);
        }
    };

    const handleConfirm = () => {
        if (formData.adults === "" || formData.adults <= 0) {
            enqueueSnackbar("Please enter the number of adults", {
                variant: "error",
            });
            return;
        }
        setOpen(false);
        // Convert empty strings to appropriate defaults for the final result
        const finalData = {
            ...formData,
            adults: formData.adults === "" ? 1 : formData.adults,
            kids: formData.kids === "" ? 0 : formData.kids,
            babies: formData.babies === "" ? 0 : formData.babies,
        };
        onResolve(finalData);
    };

    const handleCancel = () => {
        setOpen(false);
        onResolve(null);
    };

    const handleKeyDown = (event) => {
        // Allow Ctrl+Enter or Cmd+Enter to submit
        if ((event.ctrlKey || event.metaKey) && event.key === "Enter") {
            event.preventDefault();
            event.stopPropagation();
            handleConfirm();
        }
    };

    return (
        <StyledEngineProvider injectFirst>
            <ThemeProvider>
                <Dialog
                    open={open}
                    onClose={handleCancel}
                    maxWidth="sm"
                    fullWidth
                    TransitionComponent={Fade}
                    transitionDuration={{ enter: 400, exit: 300 }}
                    PaperProps={{
                        sx: (theme) => ({
                            boxShadow:
                                theme.customShadows?.dialog ||
                                theme.shadows[24],
                            overflow: "visible",
                        }),
                    }}
                    sx={{
                        "& .MuiBackdrop-root": {
                            backgroundColor: "rgba(0, 0, 0, 0.6)",
                            backdropFilter: "blur(4px)",
                        },
                    }}
                >
                    <DialogTitle
                        sx={(theme) => ({
                            pb: 1,
                            color: theme.palette.text.primary,
                            fontWeight: 600,
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                        })}
                    >
                        <Box
                            sx={(theme) => ({
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                width: 24,
                                height: 24,
                                backgroundColor: theme.palette.primary.lighter,
                                color: theme.palette.primary.main,
                            })}
                        >
                            <Iconify icon="eva:calendar-outline" width={14} />
                        </Box>
                        {title}
                    </DialogTitle>
                    <DialogContent sx={{ pb: 1 }}>
                        <DialogContentText
                            sx={(theme) => ({
                                mb: 2.5,
                                color: theme.palette.text.secondary,
                                fontSize: "0.95rem",
                                lineHeight: 1.5,
                            })}
                        >
                            {message}
                        </DialogContentText>

                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                gap: 2.5,
                            }}
                        >
                            {/* Guest Count Fields */}
                            <Grid container spacing={2}>
                                <Grid item xs={4}>
                                    <TextField
                                        label="Adults"
                                        type="number"
                                        fullWidth
                                        variant="outlined"
                                        value={formData.adults}
                                        onChange={(e) =>
                                            handleNumberChange(
                                                "adults",
                                                e.target.value
                                            )
                                        }
                                        onFocus={(e) => e.target.select()}
                                        onClick={(e) => e.target.select()}
                                        inputProps={{
                                            min: 1,
                                            max: 20,
                                        }}
                                        sx={(theme) => ({
                                            "& .MuiOutlinedInput-root": {
                                                "&:hover .MuiOutlinedInput-notchedOutline":
                                                    {
                                                        borderColor:
                                                            theme.palette
                                                                .primary.main,
                                                    },
                                                "&.Mui-focused .MuiOutlinedInput-notchedOutline":
                                                    {
                                                        borderColor:
                                                            theme.palette
                                                                .primary.main,
                                                        borderWidth: 2,
                                                    },
                                            },
                                        })}
                                    />
                                </Grid>
                                <Grid item xs={4}>
                                    <TextField
                                        label="Kids"
                                        type="number"
                                        fullWidth
                                        variant="outlined"
                                        value={formData.kids}
                                        onChange={(e) =>
                                            handleNumberChange(
                                                "kids",
                                                e.target.value
                                            )
                                        }
                                        onFocus={(e) => e.target.select()}
                                        onClick={(e) => e.target.select()}
                                        inputProps={{
                                            min: 0,
                                            max: 20,
                                        }}
                                        sx={(theme) => ({
                                            "& .MuiOutlinedInput-root": {
                                                "&:hover .MuiOutlinedInput-notchedOutline":
                                                    {
                                                        borderColor:
                                                            theme.palette
                                                                .primary.main,
                                                    },
                                                "&.Mui-focused .MuiOutlinedInput-notchedOutline":
                                                    {
                                                        borderColor:
                                                            theme.palette
                                                                .primary.main,
                                                        borderWidth: 2,
                                                    },
                                            },
                                        })}
                                    />
                                </Grid>
                                <Grid item xs={4}>
                                    <TextField
                                        label="Babies"
                                        type="number"
                                        fullWidth
                                        variant="outlined"
                                        value={formData.babies}
                                        onChange={(e) =>
                                            handleNumberChange(
                                                "babies",
                                                e.target.value
                                            )
                                        }
                                        onFocus={(e) => e.target.select()}
                                        onClick={(e) => e.target.select()}
                                        inputProps={{
                                            min: 0,
                                            max: 20,
                                        }}
                                        sx={(theme) => ({
                                            "& .MuiOutlinedInput-root": {
                                                "&:hover .MuiOutlinedInput-notchedOutline":
                                                    {
                                                        borderColor:
                                                            theme.palette
                                                                .primary.main,
                                                    },
                                                "&.Mui-focused .MuiOutlinedInput-notchedOutline":
                                                    {
                                                        borderColor:
                                                            theme.palette
                                                                .primary.main,
                                                        borderWidth: 2,
                                                    },
                                            },
                                        })}
                                    />
                                </Grid>
                            </Grid>

                            {/* Notes Field */}
                            <TextField
                                autoFocus
                                label="Notes"
                                fullWidth
                                variant="outlined"
                                value={formData.notes}
                                onChange={(e) =>
                                    handleInputChange("notes", e.target.value)
                                }
                                onKeyDown={handleKeyDown}
                                placeholder="Enter any special notes or requirements..."
                                multiline
                                rows={3}
                                helperText="Press Ctrl+Enter to submit quickly"
                                sx={(theme) => ({
                                    "& .MuiOutlinedInput-root": {
                                        "&:hover .MuiOutlinedInput-notchedOutline":
                                            {
                                                borderColor:
                                                    theme.palette.primary.main,
                                            },
                                        "&.Mui-focused .MuiOutlinedInput-notchedOutline":
                                            {
                                                borderColor:
                                                    theme.palette.primary.main,
                                                borderWidth: 2,
                                            },
                                    },
                                    "& .MuiFormHelperText-root": {
                                        color: theme.palette.text.disabled,
                                        fontSize: "0.8rem",
                                    },
                                })}
                            />
                        </Box>
                    </DialogContent>
                    <DialogActions sx={{ px: 3, pb: 3, pt: 1, gap: 1.5 }}>
                        <Button
                            onClick={handleCancel}
                            variant="outlined"
                            sx={(theme) => ({
                                borderColor: theme.palette.grey[300],
                                color: theme.palette.grey[600],
                                "&:hover": {
                                    borderColor: theme.palette.grey[400],
                                    backgroundColor: theme.palette.grey[50],
                                },
                            })}
                        >
                            {cancelText}
                        </Button>
                        <Button
                            onClick={handleConfirm}
                            variant="contained"
                            color="primary"
                            sx={(theme) => ({
                                minWidth: 100,
                                boxShadow:
                                    theme.customShadows?.primary ||
                                    theme.shadows[8],
                                "&:hover": {
                                    boxShadow:
                                        theme.customShadows?.primary ||
                                        theme.shadows[12],
                                },
                            })}
                        >
                            {confirmText}
                        </Button>
                    </DialogActions>
                </Dialog>
            </ThemeProvider>
        </StyledEngineProvider>
    );
};

export const showReservationPrompt = (options = {}) => {
    return new Promise((resolve, reject) => {
        // Create a container for the dialog
        const container = document.createElement("div");
        document.body.appendChild(container);

        const root = ReactDOM.createRoot(container);

        const cleanup = () => {
            // Cleanup after dialog closes
            setTimeout(() => {
                root.unmount();
                document.body.removeChild(container);
            }, 300); // Small delay to allow dialog close animation
        };

        const handleResolve = (formData) => {
            cleanup();
            resolve(formData);
        };

        const handleReject = (error) => {
            cleanup();
            reject(error);
        };

        root.render(
            <ReservationPromptDialog
                onResolve={handleResolve}
                onReject={handleReject}
                options={options}
            />
        );
    });
};
